/**
 * Reading annotations off a photo, whatever era they were written in.
 *
 * `Photo.annotations` is an untyped JSONField that has held two shapes:
 *
 *   v1  `Record<id, { type, points: {x,y}[], style: {...}, ... }>`
 *       Device pixels against a `screenWidth - 32` wide, 4:3 box, with the
 *       photo drawn `cover` — so the numbers depend on the phone that wrote
 *       them and describe a crop of the image rather than the image.
 *   v2  `{ schema: 2, items: [...] }`
 *       Image-relative. See `annotationSchema.ts`.
 *
 * Everything that reads annotations goes through `readAnnotations` and gets v2
 * back. Nothing else in the app should know v1 exists.
 *
 * ## What makes the v1 conversion tractable
 *
 * Only one geometry was ever possible. `PhotoDetailScreen` computes a landscape
 * box size, but both its full-screen and landscape branches return before the
 * canvas renders — so every v1 annotation in existence was drawn in portrait at
 * `W = screenWidth - 32`, `H = 0.75 * W`. One formula, not a family of them.
 *
 * ## What makes it approximate
 *
 * We do not know which phone drew it. `legacyBoxWidth` infers a lower bound
 * from the data itself, which is exact on the overwhelmingly common
 * same-device path and degrades to a bounded scale error otherwise. That is
 * the deliberate trade: annotations may shift by a few percent for a rep who
 * changed phones, versus losing them entirely.
 *
 * Reading never writes. A photo's stored payload is upgraded only when someone
 * opens the editor and saves, so merely browsing a job cannot rewrite history.
 */

import {
  DEFAULT_SIZE,
  DEFAULT_WIDTH,
  EMPTY_DOC,
  MAX_FREEHAND_POINTS,
  RDP_EPSILON,
  SCHEMA_VERSION,
  clamp01,
  normalizeColor,
  normalizeLegacyColor,
  normalizeText,
  round4,
  type AnnotationDoc,
  type AnnotationItem,
  type NPoint,
} from './annotationSchema';
import { decimateToMax, simplifyRDP } from './annotationGeometry';

/** Horizontal padding the old canvas sat inside (`padding: 16` on both sides). */
export const LEGACY_H_PADDING = 32;
/** The old canvas was always `height = width * 3/4`. */
export const LEGACY_BOX_ASPECT = 4 / 3;
/** Used only when the caller cannot supply a screen width. */
export const DEFAULT_REFERENCE_WIDTH = 390;

export interface MigrateContext {
  /** `Dimensions.get('window').width` at read time. */
  screenWidth: number;
  /** `imageWidth / imageHeight` of the real photo, or null when unmeasurable. */
  imageAspect: number | null;
}

// --- Coordinate conversion ---

/**
 * Undo the `cover` crop.
 *
 * `cover` scales the photo until it fills the box and throws away the overflow
 * on the long axis, so a point at the left edge of the box was never at the
 * left edge of the photo. `visible` is the fraction of the photo that survived
 * the crop; half the remainder is the offset.
 *
 * With no known aspect ratio there is nothing to undo, so this is the identity
 * — which keeps annotations exactly where v1 put them rather than moving them
 * somewhere confidently wrong.
 */
export function invertCoverCrop(
  fx: number,
  fy: number,
  imageAspect: number | null
): NPoint {
  if (!imageAspect || !Number.isFinite(imageAspect) || imageAspect <= 0) {
    return [fx, fy];
  }
  if (imageAspect > LEGACY_BOX_ASPECT) {
    // Photo is relatively wider than the box: it was scaled to the box height
    // and cropped left and right.
    const visible = LEGACY_BOX_ASPECT / imageAspect;
    return [(1 - visible) / 2 + fx * visible, fy];
  }
  // Photo is relatively taller: scaled to the box width, cropped top and bottom.
  const visible = imageAspect / LEGACY_BOX_ASPECT;
  return [fx, (1 - visible) / 2 + fy * visible];
}

function collectPoints(value: unknown, out: { x: number; y: number }[]): void {
  if (!value || typeof value !== 'object') return;
  const record = value as Record<string, unknown>;
  const points = record.points;
  if (Array.isArray(points)) {
    for (const p of points) {
      if (p && typeof p === 'object') {
        const { x, y } = p as { x?: unknown; y?: unknown };
        if (typeof x === 'number' && typeof y === 'number') out.push({ x, y });
      }
    }
  }
  for (const key of ['position', 'startPoint', 'endPoint']) {
    const p = record[key];
    if (p && typeof p === 'object') {
      const { x, y } = p as { x?: unknown; y?: unknown };
      if (typeof x === 'number' && typeof y === 'number') out.push({ x, y });
    }
  }
}

/**
 * Infer the width of the canvas the v1 annotations were drawn on.
 *
 * The old canvas clamped every captured point into `[0, boxW] x [0, boxH]`, so
 * the largest coordinate present is a hard lower bound on the true box. Taking
 * the max of that and the current device's box width means:
 *
 *   - same phone (the usual case) — exact;
 *   - a wider phone now — we still use the current width, which is correct;
 *   - a *narrower* phone now — the observed extent rescues us, so no
 *     coordinate normalizes past 1.0 and nothing gets clamped off the image.
 */
export function legacyBoxWidth(
  record: Record<string, unknown>,
  screenWidth: number
): number {
  const base =
    Number.isFinite(screenWidth) && screenWidth > 0
      ? screenWidth - LEGACY_H_PADDING
      : DEFAULT_REFERENCE_WIDTH - LEGACY_H_PADDING;

  const points: { x: number; y: number }[] = [];
  for (const value of Object.values(record)) collectPoints(value, points);

  let maxX = 0;
  let maxY = 0;
  for (const p of points) {
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }

  // boxH = boxW / LEGACY_BOX_ASPECT, so a y bound implies boxW >= y * aspect.
  return Math.max(base, maxX, maxY * LEGACY_BOX_ASPECT);
}

interface LegacyBox {
  boxW: number;
  boxH: number;
  /** Size of the *whole* photo as it was rendered under cover, in box pixels. */
  fullW: number;
  fullH: number;
  fullDiag: number;
  aspect: number | null;
}

function buildLegacyBox(
  record: Record<string, unknown>,
  ctx: MigrateContext
): LegacyBox {
  const boxW = legacyBoxWidth(record, ctx.screenWidth);
  const boxH = boxW / LEGACY_BOX_ASPECT;
  const aspect =
    ctx.imageAspect && Number.isFinite(ctx.imageAspect) && ctx.imageAspect > 0
      ? ctx.imageAspect
      : null;

  let fullW: number;
  let fullH: number;
  if (aspect === null) {
    fullW = boxW;
    fullH = boxH;
  } else if (aspect > LEGACY_BOX_ASPECT) {
    fullH = boxH;
    fullW = boxH * aspect;
  } else {
    fullW = boxW;
    fullH = boxW / aspect;
  }

  return {
    boxW,
    boxH,
    fullW,
    fullH,
    fullDiag: Math.sqrt(fullW * fullW + fullH * fullH),
    aspect,
  };
}

function toNormalized(
  point: { x: number; y: number },
  box: LegacyBox
): NPoint {
  const [u, v] = invertCoverCrop(point.x / box.boxW, point.y / box.boxH, box.aspect);
  return [clamp01(u), clamp01(v)];
}

// --- v1 item conversion ---

interface V1Point {
  x: number;
  y: number;
}

function readV1Point(raw: unknown): V1Point | null {
  if (!raw || typeof raw !== 'object') return null;
  const { x, y } = raw as { x?: unknown; y?: unknown };
  if (typeof x !== 'number' || typeof y !== 'number') return null;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

function readV1Points(raw: unknown): V1Point[] {
  if (!Array.isArray(raw)) return [];
  const out: V1Point[] = [];
  for (const p of raw) {
    const point = readV1Point(p);
    if (point) out.push(point);
  }
  return out;
}

function migrateV1Item(raw: unknown, box: LegacyBox): AnnotationItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;

  const id = typeof a.id === 'string' && a.id ? a.id : null;
  const type = a.type;
  if (!id || typeof type !== 'string') return null;

  const style = (a.style && typeof a.style === 'object' ? a.style : {}) as Record<
    string,
    unknown
  >;
  const color = normalizeLegacyColor(style.color);
  const strokeWidthPx =
    typeof style.strokeWidth === 'number' && style.strokeWidth > 0
      ? style.strokeWidth
      : 4;
  const fontSizePx =
    typeof style.fontSize === 'number' && style.fontSize > 0 ? style.fontSize : 18;

  const width = round4(strokeWidthPx / box.fullDiag) || DEFAULT_WIDTH;
  const size = round4(fontSizePx / box.fullDiag) || DEFAULT_SIZE;

  const createdAt = typeof a.createdAt === 'string' ? a.createdAt : new Date(0).toISOString();
  const updatedAt = typeof a.updatedAt === 'string' ? a.updatedAt : createdAt;
  const base = { id, color, createdAt, updatedAt };

  const points = readV1Points(a.points);

  if (type === 'text') {
    const anchor = points[0] ?? readV1Point(a.position);
    const text = normalizeText(a.text);
    if (!anchor || !text) return null;
    return {
      ...base,
      kind: 'text',
      at: toNormalized(anchor, box),
      text,
      size,
      // v1 rendered plain glyphs with no backing, which disappeared over a
      // bright roof. A halo is the closest thing to what the rep intended.
      halo: true,
    };
  }

  if (type === 'freehand') {
    if (points.length === 0) return null;
    const normalized = points.map((p) => toNormalized(p, box));
    return {
      ...base,
      kind: 'freehand',
      width,
      points: compressFreehand(normalized),
    };
  }

  const start = points[0] ?? readV1Point(a.startPoint);
  const end = points[1] ?? readV1Point(a.endPoint) ?? start;
  if (!start || !end) return null;

  if (type === 'circle') {
    const p1 = toNormalized(start, box);
    const p2 = toNormalized(end, box);
    let rx = Math.abs(p2[0] - p1[0]) / 2;
    let ry = Math.abs(p2[1] - p1[1]) / 2;
    // A tap drew a fixed 10px circle in v1; without this it would migrate to a
    // zero-radius ellipse and silently vanish.
    if (rx === 0 && ry === 0) {
      rx = 10 / box.fullW;
      ry = 10 / box.fullH;
    }
    return {
      ...base,
      kind: 'ellipse',
      width,
      center: [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2],
      rx,
      ry,
    };
  }

  if (type === 'arrow') {
    const from = toNormalized(start, box);
    let to = toNormalized(end, box);
    // Same story: a tap drew a 20px arrow pointing right.
    if (from[0] === to[0] && from[1] === to[1]) {
      to = [clamp01(from[0] + 20 / box.fullW), from[1]];
    }
    return { ...base, kind: 'arrow', width, from, to };
  }

  return null;
}

/**
 * Drop collinear noise, then hard-cap as a payload backstop.
 *
 * The pre-decimation matters: RDP is O(n^2) when the stroke is a zigzag that
 * keeps every vertex, and this runs once per photo while a job's grid scrolls.
 * A stroke with thousands of samples carries far more detail than a 400-point
 * cap can keep anyway, so bounding the input first costs nothing visually and
 * turns a worst case of tens of millions of distance computations into a few
 * hundred thousand.
 */
export function compressFreehand(points: NPoint[]): NPoint[] {
  const bounded =
    points.length > MAX_FREEHAND_POINTS * 2
      ? decimateToMax(points, MAX_FREEHAND_POINTS * 2)
      : points;
  return decimateToMax(simplifyRDP(bounded, RDP_EPSILON), MAX_FREEHAND_POINTS);
}

// --- v2 validation ---

function readNPoint(raw: unknown): NPoint | null {
  if (Array.isArray(raw) && raw.length >= 2) {
    const [x, y] = raw;
    if (typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y)) {
      return [clamp01(x), clamp01(y)];
    }
    return null;
  }
  // Tolerated so a half-written document does not silently lose a rep's work.
  const asObject = readV1Point(raw);
  return asObject ? [clamp01(asObject.x), clamp01(asObject.y)] : null;
}

function positiveOr(raw: unknown, fallback: number): number {
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : fallback;
}

function readV2Item(raw: unknown): AnnotationItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  const id = typeof a.id === 'string' && a.id ? a.id : null;
  if (!id) return null;

  const createdAt = typeof a.createdAt === 'string' ? a.createdAt : new Date(0).toISOString();
  const base = {
    id,
    color: normalizeColor(a.color),
    createdAt,
    updatedAt: typeof a.updatedAt === 'string' ? a.updatedAt : createdAt,
  };
  const width = positiveOr(a.width, DEFAULT_WIDTH);

  switch (a.kind) {
    case 'arrow': {
      const from = readNPoint(a.from);
      const to = readNPoint(a.to);
      if (!from || !to) return null;
      return { ...base, kind: 'arrow', width, from, to };
    }
    case 'ellipse': {
      const center = readNPoint(a.center);
      if (!center) return null;
      const rx = typeof a.rx === 'number' && Number.isFinite(a.rx) ? Math.abs(a.rx) : 0;
      const ry = typeof a.ry === 'number' && Number.isFinite(a.ry) ? Math.abs(a.ry) : 0;
      if (rx === 0 && ry === 0) return null;
      return { ...base, kind: 'ellipse', width, center, rx, ry };
    }
    case 'freehand': {
      if (!Array.isArray(a.points)) return null;
      const points: NPoint[] = [];
      for (const p of a.points) {
        const point = readNPoint(p);
        if (point) points.push(point);
      }
      if (points.length === 0) return null;
      return { ...base, kind: 'freehand', width, points: compressFreehand(points) };
    }
    case 'text': {
      const at = readNPoint(a.at);
      const text = normalizeText(a.text);
      if (!at || !text) return null;
      return {
        ...base,
        kind: 'text',
        at,
        text,
        size: positiveOr(a.size, DEFAULT_SIZE),
        halo: a.halo !== false,
      };
    }
    default:
      return null;
  }
}

// --- Public API ---

function isV2(raw: unknown): raw is { schema: number; items: unknown[] } {
  return (
    !!raw &&
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    (raw as { schema?: unknown }).schema === SCHEMA_VERSION &&
    Array.isArray((raw as { items?: unknown }).items)
  );
}

/**
 * The single entry point for turning stored annotations into something
 * renderable. Accepts v2, v1, null, and outright junk; never throws.
 */
export function readAnnotations(raw: unknown, ctx: MigrateContext): AnnotationDoc {
  try {
    if (isV2(raw)) {
      const items: AnnotationItem[] = [];
      for (const entry of raw.items) {
        const item = readV2Item(entry);
        if (item) items.push(item);
      }
      return serializeDoc({ schema: SCHEMA_VERSION, items });
    }

    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return EMPTY_DOC;

    const record = raw as Record<string, unknown>;
    const box = buildLegacyBox(record, ctx);

    // v1 relied on `Object.values()` insertion order for z-order, which is an
    // implementation detail. Sort by creation time and fall back to key order.
    const entries = Object.values(record)
      .map((value, index) => ({ value, index }))
      .sort((a, b) => {
        const at = Date.parse(
          (a.value as { createdAt?: string })?.createdAt ?? ''
        );
        const bt = Date.parse(
          (b.value as { createdAt?: string })?.createdAt ?? ''
        );
        const aValid = Number.isFinite(at);
        const bValid = Number.isFinite(bt);
        if (aValid && bValid && at !== bt) return at - bt;
        if (aValid !== bValid) return aValid ? -1 : 1;
        return a.index - b.index;
      });

    const items: AnnotationItem[] = [];
    for (const entry of entries) {
      const item = migrateV1Item(entry.value, box);
      if (item) items.push(item);
    }
    return serializeDoc({ schema: SCHEMA_VERSION, items });
  } catch {
    // A malformed payload must degrade to "no annotations", never to a crash
    // in the middle of a job's photo grid.
    return EMPTY_DOC;
  }
}

/**
 * Does this photo have anything drawn on it?
 *
 * This exists because `Object.keys(annotations).length > 0` — the check the
 * photo grid and the opportunity counter used — is **true** for a v2 document
 * with an empty `items` array. Without a shared predicate, every photo that
 * had ever been through the editor would wear an "ANNOTATED" badge.
 *
 * Deliberately cheap and context-free: it runs per card while a grid scrolls.
 */
export function hasAnnotations(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;

  if (isV2(raw)) {
    return raw.items.some(
      (item) =>
        !!item &&
        typeof item === 'object' &&
        typeof (item as { kind?: unknown }).kind === 'string'
    );
  }

  return Object.values(raw as Record<string, unknown>).some((value) => {
    if (!value || typeof value !== 'object') return false;
    const a = value as Record<string, unknown>;
    return (
      typeof a.id === 'string' &&
      typeof a.type === 'string' &&
      Array.isArray(a.points) &&
      a.points.length > 0 &&
      !!a.style
    );
  });
}

function serializeItem(item: AnnotationItem): AnnotationItem {
  const point = (p: NPoint): NPoint => [round4(p[0]), round4(p[1])];
  switch (item.kind) {
    case 'arrow':
      return {
        id: item.id,
        kind: 'arrow',
        color: item.color,
        width: round4(item.width),
        from: point(item.from),
        to: point(item.to),
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      };
    case 'ellipse':
      return {
        id: item.id,
        kind: 'ellipse',
        color: item.color,
        width: round4(item.width),
        center: point(item.center),
        rx: round4(item.rx),
        ry: round4(item.ry),
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      };
    case 'freehand':
      return {
        id: item.id,
        kind: 'freehand',
        color: item.color,
        width: round4(item.width),
        points: item.points.map(point),
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      };
    case 'text':
      return {
        id: item.id,
        kind: 'text',
        color: item.color,
        at: point(item.at),
        text: item.text,
        size: round4(item.size),
        halo: item.halo,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      };
  }
}

/**
 * Canonical form: fixed key order, coordinates rounded. Both the payload we
 * PATCH and the input to `docFingerprint`, so "what we compared" and "what we
 * saved" can never drift apart.
 */
export function serializeDoc(doc: AnnotationDoc): AnnotationDoc {
  return { schema: SCHEMA_VERSION, items: doc.items.map(serializeItem) };
}

/** Stable string for dirty-checking. Equal fingerprints mean nothing to save. */
export function docFingerprint(doc: AnnotationDoc): string {
  return JSON.stringify(serializeDoc(doc));
}
